import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Alert, CircularProgress } from "@mui/material";
import { TorchButton } from "./torch-button";
import { CameraSwitchButton } from "./camera-switch-button";
import { BackButton } from "./back-button";
import { PortraitLock } from "../portrait-lock";
import { useCamera } from "./use-camera";
import { ImagePreview } from "../viewer/image-preview";

// keep the stored photo a reasonable size for localStorage.
const MAX_PHOTO_SIZE = 1600; // px, longer side
const PHOTO_QUALITY = 0.85; // JPEG — PNG photos are too big for localStorage

// Fullscreen live-camera view capturing a single photo.
export const ImageCaptureView = ({ onSaved, onBack }) => {
  // camera stream, torch, and front/rear switching.
  // Unlike the scan view, the saved scan settings are not used:
  // always default the rear camera, and torch off.
  const {
    attachVideo,
    error,
    preparing,
    facingMode,
    switchCamera,
    torchOn,
    torchSupported,
    toggleTorch,
  } = useCamera();

  // the taken photo as a JPEG data URL, or null while the camera shows.
  const [photo, setPhoto] = useState(null);

  // keep our own handle on the <video> to grab its frame for the photo.
  const videoRef = useRef(null);
  const attach = useCallback(
    (node) => {
      videoRef.current = node;
      attachVideo(node);
    },
    [attachVideo],
  );

  // no need of the torch while previewing the taken photo (it stays off on a
  // retake — the user can turn it back on).
  useEffect(() => {
    if (photo && torchOn) toggleTorch(false);
  }, [photo, torchOn, toggleTorch]);

  // grab the current video frame as the photo, downscaled to a storable size.
  function takePhoto() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const scale = Math.min(
      1,
      MAX_PHOTO_SIZE / Math.max(video.videoWidth, video.videoHeight),
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhoto(canvas.toDataURL("image/jpeg", PHOTO_QUALITY));
  }

  // preview the taken photo (the camera keeps running underneath, so a
  // retake is instant).
  if (photo) {
    return (
      <>
        <PortraitLock />
        <ImagePreview
          src={photo}
          onDelete={() => setPhoto(null)}
          onSave={() => onSaved(photo)}
        />
      </>
    );
  }

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        backgroundColor: "#000",
        overflow: "hidden",
        zIndex: 1300,
      }}
    >
      <PortraitLock />
      <Box
        component="video"
        ref={attach}
        autoPlay
        playsInline
        muted
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          // un-flip the front camera's preview
          transform: facingMode === "user" ? "scaleX(-1)" : "none",
        }}
      />

      {/* the camera can take a few seconds to get ready on some phones */}
      {preparing && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "#000",
            color: "#fff",
            px: 4,
            textAlign: "center",
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6">Getting the camera ready…</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            This may take a few seconds. Please hold on.
          </Typography>
        </Box>
      )}

      {/* back to the dashboard at the top left */}
      <Box
        sx={{
          position: "absolute",
          top: "max(24px, env(safe-area-inset-top))",
          left: 16,
        }}
      >
        <BackButton onClick={onBack} />
      </Box>

      {/* camera switch at the top right */}
      <Box
        sx={{
          position: "absolute",
          top: "max(24px, env(safe-area-inset-top))",
          right: 16,
        }}
      >
        <CameraSwitchButton onClick={switchCamera} />
      </Box>

      {error && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 16,
            right: 16,
            transform: "translateY(-50%)",
          }}
        >
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* round shutter button at the bottom center */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          pb: 5,
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label="Take photo"
          onClick={takePhoto}
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            backgroundColor: "rgba(0,0,0,0.2)",
            border: "2px solid rgba(255,255,255,0.9)",
            cursor: "pointer",
            p: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: "primary.main",
            }}
          />
        </Box>
      </Box>

      {/* torch at the bottom right, centered on the shutter button row */}
      {torchSupported && (
        <Box
          sx={{
            position: "absolute",
            right: 16,
            bottom: 56,
          }}
        >
          <TorchButton on={torchOn} enabled onToggle={toggleTorch} />
        </Box>
      )}
    </Box>
  );
};

ImageCaptureView.propTypes = {
  onSaved: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
